import { useEffect, useMemo, useRef, useState } from "react";
import {
  enrichSchoolListItemsProgressively,
  getCachedSchoolListItem,
  primeSchoolListItemCache,
} from "@/project/karuna/utils/schoolProfileEnrichment";
import type { SchoolDisplayFields } from "@/project/karuna/utils/schoolProfileRecordFields";

type SchoolListItem = { id: string } & SchoolDisplayFields;

function resolvePageSchools<T extends SchoolListItem>(pagedSchools: T[]): T[] {
  return pagedSchools.map(
    school => (getCachedSchoolListItem(school.id) as T | undefined) ?? school
  );
}

/**
 * Shows the current page immediately, then fills in sparse card fields as
 * detail fetches complete (no multi-second wait for the whole page).
 */
export function useEnrichedSchoolPage<T extends SchoolListItem>(
  pagedSchools: T[],
  listRecordsById: Map<string, Record<string, unknown>>
): T[] {
  const pageIdsKey = useMemo(
    () => pagedSchools.map(school => school.id).join(","),
    [pagedSchools]
  );

  const listRecordsRef = useRef(listRecordsById);
  listRecordsRef.current = listRecordsById;

  const pagedSchoolsRef = useRef(pagedSchools);
  pagedSchoolsRef.current = pagedSchools;

  const [displaySchools, setDisplaySchools] = useState<T[]>(() =>
    resolvePageSchools(pagedSchools)
  );

  useEffect(() => {
    const page = pagedSchoolsRef.current;
    setDisplaySchools(resolvePageSchools(page));

    if (page.length === 0) {
      return;
    }

    let cancelled = false;

    void enrichSchoolListItemsProgressively(
      page,
      listRecordsRef.current,
      enriched => {
        if (cancelled) {
          return;
        }
        primeSchoolListItemCache(enriched);
        setDisplaySchools(prev =>
          prev.map(school =>
            school.id === enriched.id ? (enriched as T) : school
          )
        );
      }
    );

    return () => {
      cancelled = true;
    };
  }, [pageIdsKey]);

  return displaySchools;
}
